import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

// Register GSAP plugin
gsap.registerPlugin(CustomEase);

// Define custom ease
CustomEase.create('main', '0.65, 0.01, 0.05, 0.99');

gsap.defaults({
  ease: 'main',
  duration: 0.5,
});

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  hideTrigger?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, hideTrigger = false }) => {
  const navRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLElement>(null);
  const menuLinksRef = useRef<NodeListOf<HTMLAnchorElement> | null>(null);
  const fadeTargetsRef = useRef<NodeListOf<HTMLElement> | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const routes = [
    { path: '/', label: 'Home', badge: '' },
    { path: '/about', label: 'About', badge: '' },
    { path: '/services', label: 'Services', badge: '' },
    { path: '/clientportal', label: 'Client Portal', badge: 'New' },
    { path: '/Archive', label: 'Archive', badge: '36' },
    { path: '/contact', label: 'Contact', badge: '' },
    { path: '/testimonials', label: 'Testimonials', badge: '' },
    { path: '/faq', label: 'FAQ', badge: '' },
  ];

  const socialLinks = [
    { href: 'https://www.instagram.com/nuviosites', label: 'Instagram' },
    { href: 'https://www.linkedin.com/in/nuviosites', label: 'LinkedIn' },
  ];

  // Independent effect to handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  useEffect(() => {
    if (!navRef.current || !overlayRef.current || !menuRef.current) return;

    // Collect NodeLists dynamically to ensure we have latest refs
    const menuLinks = navRef.current.querySelectorAll('.menu-link');
    const fadeTargets = navRef.current.querySelectorAll('[data-menu-fade]');

    const tl = gsap.timeline();

    if (isOpen) {
      // OPEN ANIMATION
      tl.clear()
        .to(overlayRef.current, { opacity: 1, duration: 0.5 })
        .fromTo(
          menuRef.current,
          { opacity: 0, scale: 1.05 },
          { opacity: 1, scale: 1, duration: 0.4, ease: "power2.out" },
          '<'
        )
        .fromTo(
          menuLinks,
          { yPercent: 100, opacity: 0 },
          { yPercent: 0, opacity: 1, stagger: 0.05 },
          '<+=0.1'
        )
        .fromTo(
          fadeTargets,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, stagger: 0.05 },
          '<+=0.1'
        );
    } else {
      // CLOSE ANIMATION
      tl.clear()
        .to(overlayRef.current, { opacity: 0, duration: 0.3 })
        .to(menuRef.current, { opacity: 0, scale: 1.05, duration: 0.3 }, '<');
    }

    // Optional: Animate internal trigger button if present
    if (buttonRef.current) {
      if (isOpen) {
        gsap.to(buttonRef.current, { backgroundColor: '#000000', duration: 0.3 });
        gsap.to('.hamburger-line', { backgroundColor: '#ffffff', duration: 0.3 });
      } else {
        gsap.to(buttonRef.current, { backgroundColor: '#ffffff', duration: 0.3 });
        gsap.to('.hamburger-line', { backgroundColor: '#000000', duration: 0.3 });
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onToggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onToggle]);

  return (
    <>
      <style>
        {`
          @font-face {
            font-family: 'PP Neue Corp Tight';
            src: url('https://cdn.prod.website-files.com/673af51dea86ab95d124c3ee/673b0f5784f7060c0ac05534_PPNeueCorp-TightUltrabold.otf') format('opentype');
            font-weight: 700;
            font-style: normal;
            font-display: swap;
          }

          .nav {
            z-index: 9999;
            width: 100vw;
            height: 100vh;
            position: fixed;
            top: 0;
            left: 0;
            overflow: hidden;
            display: none; /* Hide by default */
            opacity: 0;
            transition: opacity 0s linear 0.5s; /* Delay hiding opacity for close anim */
          }
          
          /* Active state handled by React class */
          .nav.active {
            display: block;
            opacity: 1;
            transition: opacity 0s linear; /* Instant show */
            pointer-events: auto;
          }

          .overlay {
            z-index: 0;
            cursor: pointer;
            background-color: rgba(0, 0, 0, 0.4);
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
            opacity: 0;
          }

          .menu {
            width: 100%;
            height: 100%;
            background-color: #000000;
            color: #ffffff;
            position: absolute;
            right: 0;
            top: 0;
            left: 0; /* Cover full width */
            display: flex;
            flex-direction: column;
            opacity: 0; /* Start hidden for GSAP to fade in */
          }

          .menu-inner {
            width: 100%;
            height: 100%;
            display: grid;
            /* Default Mobile Layout */
            grid-template-areas: 
              "header"
              "nav"
              "footer";
            grid-template-columns: 1fr;
            grid-template-rows: auto 1fr auto;
            padding: 2rem;
            box-sizing: border-box;
          }

          .menu-header {
            grid-area: header;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }
          
          .menu-logo {
            width: 140px;
            height: auto;
            object-fit: contain;
          }

          .menu-list-container {
            grid-area: nav;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .menu-list {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .menu-link {
            text-decoration: none;
            font-family: 'PP Neue Corp Tight', sans-serif;
            font-size: 1.8rem; /* Big font for mobile */
            color: #ffffff;
            display: inline-flex;
            align-items: center;
            gap: 1rem;
            transition: color 0.3s;
            position: relative;
            text-transform: uppercase;
          }

          .menu-link:hover {
            color: #a1a1aa;
          }

          .badge {
            font-size: 0.75rem;
            padding: 0.2rem 0.5rem;
            border-radius: 4px;
            background-color: #333;
            color: #fff;
            vertical-align: middle;
            font-family: 'Inter', sans-serif;
            font-weight: normal;
          }
           
          .badge.new {
            background-color: #d4e157; /* Limeish green */
            color: #000;
            font-weight: bold;
          }

          .menu-footer {
            grid-area: footer;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-top: 2rem;
          }
          
          /* Socials Styling for Mobile */
          .social-link {
            color: #ffffff;
            font-size: 1rem;
            text-decoration: none;
            display: block;
            font-family: 'Inter', sans-serif;
          }

          /* DESKTOP LAYOUT (min-width: 1024px) */
          @media screen and (min-width: 1024px) {
            .menu-inner {
              grid-template-areas: "logo nav socials";
              grid-template-columns: 250px 1fr 200px;
              grid-template-rows: 1fr;
              padding: 4rem 6rem;
              align-items: flex-start; /* Align top */
            }

            .menu-header {
              grid-area: logo;
              display: block; /* Just logo column */
            }

            .menu-list-container {
              grid-area: nav;
              display: flex;
              flex-direction: column;
              align-items: flex-start; 
              margin-left: 10vw; 
            }

            .menu-list {
              gap: 0.5rem;
            }

            .menu-link {
              font-size: 2.6rem; /* Larger on desktop */
              line-height: 1.1;
            }

            .menu-footer {
              grid-area: socials;
              margin-top: 6rem; 
              align-items: flex-start;
              gap: 0.5rem;
            }
             
            .social-link {
               font-size: 1.1rem;
            }
          }
          
          /* MOBILE Specific Adjustments */
          @media screen and (max-width: 1023px) {
             .menu-link {
                font-size: 1.4rem;
             }
             .menu-inner {
                padding: 1.5rem;
             }
             .menu-logo {
                width: 65px;
             }
          }
        `}
      </style>

      {/* Internal Trigger (mostly hidden) */}
      {!hideTrigger && (
        <button
          ref={buttonRef}
          className={`hamburger-button ${isOpen ? 'open' : ''}`}
          onClick={onToggle}
          aria-label="Toggle menu"
          style={{ position: 'fixed', top: '2rem', right: '2rem', zIndex: 10001 }}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      )}



      {/* Main Nav Container */}
      <div
        ref={navRef}
        className={`nav ${isOpen ? 'active' : ''}`}
        aria-hidden={!isOpen}
      >
        <div ref={overlayRef} className="overlay" onClick={onToggle} />
        <nav ref={menuRef} className="menu">
          <div className="menu-inner">

            {/* Header: Logo */}
            <div className="menu-header">
              <Link to="/" onClick={onToggle}>
                <img src="/New_Logoicon.png" alt="Nuviosites Logo" className="menu-logo" />
              </Link>
            </div>

            {/* Navigation Links */}
            <div className="menu-list-container">
              <ul className="menu-list">
                {routes.map((route) => (
                  <li key={route.path}>
                    <Link to={route.path} className="menu-link" onClick={onToggle}>
                      <span data-menu-fade>{route.label}</span>
                      {route.badge && (
                        <span
                          className={`badge ${route.badge === 'New' ? 'new' : ''}`}
                          data-menu-fade
                        >
                          {route.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Footer: Socials */}
            <div className="menu-footer">
              {socialLinks.map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  data-menu-fade
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;